import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress,
    FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { yupResolver } from "@hookform/resolvers/yup";
import { FieldError, FieldValues, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEstabelecimentos } from "../../../estabelecimento/hooks/useEstabelecimentos";
import { criarEstabelecimentoSchema } from "../../../estabelecimento/schemas/criarEstabelecimentoSchema";
import { RadioCard } from "../../../core/components/RadioCard";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { useAutenticacaoContext } from "../../../core/contexts/AutenticacaoContext";
import { useMascara } from "../../../core/hooks/useMascara";
import { useOrganizacoes } from "../../hooks/useOrganizacoes";
import { IPlano } from "../../../estabelecimento/interfaces/IPlano";

interface ICriarEstabelecimentoForm {
    tipoPessoa: string;
    cnpj?: string;
    cpf?: string;
    nomeFantasia: string;
    razaoSocial?: string;
    endereco: {
        cep: string;
        rua: string;
        numero: string;
        complemento?: string;
        bairro: string;
        cidade: string;
        uf: string;
    };
}

const formatarValor = (valor: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor ?? 0);

export const CriarEstabelecimentoOrganizacao = () => {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const { usuario, entrarEstabelecimento, organizacoes } = useAutenticacaoContext();
    const { criarEstabelecimento, loading } = useEstabelecimentos();
    const { listarPlanos, loading: loadingPlanos } = useOrganizacoes();
    const { mascaraCNPJ, mascaraCEP, mascaraCPF, formatarTelefoneParaPadrao } = useMascara();

    const [planos, setPlanos] = useState<IPlano[]>([]);
    const [planoUuid, setPlanoUuid] = useState<string>("");
    const [planoErro, setPlanoErro] = useState(false);
    const [valorAssinatura, setValorAssinatura] = useState<string>("");
    const [valorErro, setValorErro] = useState(false);

    const planoSelecionado = planos.find((p) => p.uuid === planoUuid);

    const organizacaoUuid = organizacoes?.[0]?.uuid;

    const {
        register, handleSubmit, watch, setValue, formState: { errors },
    } = useForm<ICriarEstabelecimentoForm>({
        resolver: yupResolver(criarEstabelecimentoSchema) as any,
        defaultValues: { tipoPessoa: "" },
        mode: "onChange",
    });

    useEffect(() => {
        (async () => setPlanos(await listarPlanos()))();
    }, []);

    const tipoPessoa = watch("tipoPessoa");
    const isPJ = tipoPessoa === "PJ";
    const formVisivel = tipoPessoa === "PJ" || tipoPessoa === "PF";
    const enderecoErrors = errors.endereco as Record<string, FieldError | undefined> | undefined;

    const handleTipoPessoaChange = (novo: "PF" | "PJ") => {
        setValue("tipoPessoa", novo, { shouldValidate: false });
        setValue("cnpj", "");
        setValue("cpf", "");
        setValue("razaoSocial", "");
    };

    const handleBuscarCep = async (cep: string) => {
        const cepLimpo = cep.replace(/\D/g, "");
        if (cepLimpo.length !== 8) return;
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
            const data = await response.json();
            if (!data.erro) {
                setValue("endereco.rua", data.logradouro || "");
                setValue("endereco.bairro", data.bairro || "");
                setValue("endereco.cidade", data.localidade || "");
                setValue("endereco.uf", data.uf || "");
            }
        } catch {
            // Silencioso - usuário pode preencher manualmente
        }
    };

    const handleSubmitForm = async (dados: FieldValues) => {
        const valorNumerico = Number(valorAssinatura.replace(",", "."));

        let invalido = false;
        if (!planoUuid) { setPlanoErro(true); invalido = true; }
        if (!valorNumerico || valorNumerico <= 0) { setValorErro(true); invalido = true; }
        if (invalido) return;

        const cepLimpo = dados.endereco.cep.replace(/\D/g, "");
        const telefoneLimpo = formatarTelefoneParaPadrao(usuario?.telefone) ?? "";

        const resultado = await criarEstabelecimento({
            tipoPessoa: dados.tipoPessoa,
            ...(dados.tipoPessoa === "PJ"
                ? { cnpj: dados.cnpj.toUpperCase().replace(/[^A-Z0-9]/g, ""), razaoSocial: dados.razaoSocial, nomeFantasia: dados.nomeFantasia }
                : { cpf: dados.cpf.replace(/\D/g, ""), nomeCompleto: usuario?.nome, nomeFantasia: dados.nomeFantasia }),
            email: usuario?.email ?? "",
            telefone: telefoneLimpo,
            usuarioCriadorUuid: usuario?.uuid ?? "",
            organizacaoUuid,
            planoUuid,
            valorAssinatura: valorNumerico,
            endereco: {
                cep: cepLimpo,
                rua: dados.endereco.rua,
                numero: dados.endereco.numero,
                complemento: dados.endereco.complemento || undefined,
                bairro: dados.endereco.bairro,
                cidade: dados.endereco.cidade,
                uf: dados.endereco.uf,
            },
        });

        if (resultado) {
            const novoEstabelecimentoUuid = resultado.estabelecimento?.uuid;
            if (novoEstabelecimentoUuid) {
                await entrarEstabelecimento(novoEstabelecimentoUuid);
            } else {
                snackbar("Estabelecimento criado com sucesso.", { severity: "success" });
                navigate("/organizacoes");
            }
        }
    };

    return (
        <Box sx={{ maxWidth: 900, mx: "auto", p: 2 }}>
            <Typography variant="h5" fontWeight="bold">Novo estabelecimento da organização</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Preencha os dados e selecione o plano para cadastrar o estabelecimento.
            </Typography>

            <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <RadioCard labelValue="PJ" labelTitle="Pessoa Jurídica" selected={tipoPessoa === "PJ"} onClick={() => handleTipoPessoaChange("PJ")} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <RadioCard labelValue="PF" labelTitle="Pessoa Física" selected={tipoPessoa === "PF"} onClick={() => handleTipoPessoaChange("PF")} fullWidth />
                    </Grid>
                </Grid>

                {formVisivel && (
                    <form onSubmit={handleSubmit(handleSubmitForm)}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                            <Accordion defaultExpanded sx={{ borderRadius: "16px", "&:before": { display: "none" } }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography variant="h6">Plano</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth error={planoErro}>
                                                <InputLabel id="plano-label">Plano*</InputLabel>
                                                <Select
                                                    labelId="plano-label"
                                                    label="Plano*"
                                                    value={planoUuid}
                                                    onChange={(e) => { setPlanoUuid(e.target.value); setPlanoErro(false); }}
                                                    disabled={loadingPlanos}
                                                >
                                                    {planos.map((plano) => (
                                                        <MenuItem key={plano.uuid} value={plano.uuid}>
                                                            {plano.nome} — {formatarValor(plano.valor)}{plano.freeTrial ? " (free trial)" : ""}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {planoErro && <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>Selecione um plano</Typography>}
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Valor da assinatura (R$)*"
                                                fullWidth
                                                value={valorAssinatura}
                                                onChange={(e) => { setValorAssinatura(e.target.value.replace(/[^\d.,]/g, "")); setValorErro(false); }}
                                                error={valorErro}
                                                helperText={
                                                    valorErro
                                                        ? "Informe o valor fechado da assinatura"
                                                        : (planoSelecionado?.valorMinimo || planoSelecionado?.valorMaximo)
                                                            ? `Faixa do plano: ${formatarValor(planoSelecionado?.valorMinimo ?? 0)} - ${formatarValor(planoSelecionado?.valorMaximo ?? 0)}`
                                                            : "Valor fechado para este estabelecimento"
                                                }
                                                inputProps={{ inputMode: "decimal" }}
                                            />
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            <Accordion defaultExpanded sx={{ borderRadius: "16px", "&:before": { display: "none" } }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography variant="h6">Dados do estabelecimento</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        {isPJ ? (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField label="CNPJ*" fullWidth {...register("cnpj")} value={mascaraCNPJ(watch("cnpj") ?? "")} error={!!errors.cnpj} helperText={<>{errors.cnpj?.message}</>} inputProps={{ maxLength: 18 }} />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField label="Nome fantasia*" fullWidth {...register("nomeFantasia")} error={!!errors.nomeFantasia} helperText={<>{errors.nomeFantasia?.message}</>} />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField label="Razão social*" fullWidth {...register("razaoSocial")} error={!!errors.razaoSocial} helperText={<>{errors.razaoSocial?.message}</>} />
                                                </Grid>
                                            </>
                                        ) : (
                                            <>
                                                <Grid item xs={12} sm={5}>
                                                    <TextField label="CPF*" fullWidth {...register("cpf")} value={mascaraCPF(watch("cpf") ?? "")} error={!!errors.cpf} helperText={<>{errors.cpf?.message}</>} inputProps={{ maxLength: 14 }} />
                                                </Grid>
                                                <Grid item xs={12} sm={7}>
                                                    <TextField label="Nome do estabelecimento*" fullWidth {...register("nomeFantasia")} error={!!errors.nomeFantasia} helperText={<>{errors.nomeFantasia?.message}</>} />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            <Accordion defaultExpanded sx={{ borderRadius: "16px", "&:before": { display: "none" } }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography variant="h6">Endereço</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <TextField label="CEP" fullWidth {...register("endereco.cep")} value={mascaraCEP(watch("endereco.cep") ?? "")} onChange={(e) => { setValue("endereco.cep", e.target.value); handleBuscarCep(e.target.value); }} inputProps={{ maxLength: 9 }} />
                                        </Grid>
                                        <Grid item xs={12} sm={8}>
                                            <TextField label="Rua*" fullWidth {...register("endereco.rua")} InputLabelProps={{ shrink: !!watch("endereco.rua") || undefined }} error={!!enderecoErrors?.rua} helperText={<>{enderecoErrors?.rua?.message}</>} />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField label="Número*" fullWidth {...register("endereco.numero")} error={!!enderecoErrors?.numero} helperText={<>{enderecoErrors?.numero?.message}</>} />
                                        </Grid>
                                        <Grid item xs={12} sm={5}>
                                            <TextField label="Complemento" fullWidth {...register("endereco.complemento")} />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField label="Bairro*" fullWidth {...register("endereco.bairro")} InputLabelProps={{ shrink: !!watch("endereco.bairro") || undefined }} error={!!enderecoErrors?.bairro} helperText={<>{enderecoErrors?.bairro?.message}</>} />
                                        </Grid>
                                        <Grid item xs={12} sm={8}>
                                            <TextField label="Cidade*" fullWidth {...register("endereco.cidade")} InputLabelProps={{ shrink: !!watch("endereco.cidade") || undefined }} error={!!enderecoErrors?.cidade} helperText={<>{enderecoErrors?.cidade?.message}</>} />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField label="UF*" fullWidth {...register("endereco.uf")} InputLabelProps={{ shrink: !!watch("endereco.uf") || undefined }} error={!!enderecoErrors?.uf} helperText={<>{enderecoErrors?.uf?.message}</>} inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }} />
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                                <Button variant="outlined" size="large" onClick={() => navigate("/organizacoes")} disabled={loading}>
                                    Cancelar
                                </Button>
                                {loading ? (
                                    <CircularProgress size={40} />
                                ) : (
                                    <Button variant="contained" size="large" type="submit">
                                        Cadastrar estabelecimento
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </form>
                )}
            </Box>
        </Box>
    );
};
